import React, {useEffect, useMemo, useState} from "react";
import {Breadcrumb, Layout, Tabs, theme, Spin} from "antd";
import {useGetProfileMutation, useGetBrandsQuery} from "../services/nestjs-base-api";
import {useAuth} from "../hooks/useAuth";
import {IOrganisation} from "../services/nestjs-base-api/types";
import OrganisationDetails from "../components/Organisation/OrganisationDetails";
import OrganisationUsers from "../components/Organisation/OrganisationUsers";
import ApiKeys from "../components/Organisation/ApiKeys";
import Billing from "../components/Organisation/Billing";
import SubscriptionOverview from "../components/Organisation/SubscriptionOverview";
import { AgencyRelationship } from "../components/Organisation/AgencyRelationship";
import BrandKnowledge from "../components/Organisation/BrandKnowledge";
// Deprecated: AI Usage tab replaced by AI Expert Credits in Subscription tab
// import { AIQuotaWidget } from "../components/AIQuotaWidget";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useApiAccessEnabled } from "../hooks/useDashboardConfig";
import { useImpersonation } from "../hooks/useImpersonation";
import { useSubscriptionFeatures } from "../hooks/useSubscriptionFeatures";

const { Content } = Layout;

interface IProps {
}

const OrganisationPage: React.FunctionComponent<IProps> = (props) => {
    const { user, logout } = useAuth();
    const intl = useIntl();
    const location = useLocation();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [getProfile] = useGetProfileMutation();
    const [profileData, setProfileData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [_error, setError] = useState<any>(null);

    useEffect(() => {
        const loadProfile = async () => {
            if (!user?.jwtToken) return;
            
            setLoading(true);
            setError(null);
            try {
                const result = await getProfile({ jwtToken: user.jwtToken }).unwrap();
                setProfileData(result);
            } catch (err) {
                setError(err);
                if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
                    logout();
                }
            } finally {
                setLoading(false);
                setInitialLoad(false);
            }
        };

        loadProfile();
    }, [user?.jwtToken, getProfile, logout]);

    const organisation = useMemo(() => {
        return profileData?.organisation as IOrganisation;
    }, [profileData]);

    // Get brands to check bypass status
    const { data: brandsData } = useGetBrandsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: organisation?.id || 0,
    }, {
        skip: !user?.jwtToken || !organisation?.id
    });

    // Use the first brand to check bypass status
    const firstBrandId = brandsData?.length ? brandsData[0].id : undefined;
    const { bypassEnabled } = useSubscriptionFeatures(firstBrandId);
    // API Keys is hidden until the public API is documented (API_ACCESS_ENABLED).
    // A platform admin impersonating the org still sees it: that is how a key
    // gets issued to a customer (InStitchu's participants poll) while the
    // surface stays off for everyone else.
    const apiAccessEnabled = useApiAccessEnabled();
    const { isImpersonating } = useImpersonation();
    const showApiKeys = apiAccessEnabled || isImpersonating;

    const detailsTab = <OrganisationDetails organisation={organisation} bypassEnabled={bypassEnabled} brands={brandsData} />;

    const usersTab = <OrganisationUsers />;

    // Deprecated: AI Usage tab replaced by AI Expert Credits in Subscription tab
    // const aiUsageTab = <AIQuotaWidget />;

    const apiKeysTab = <ApiKeys />;

    // Check if #upgrade hash or showUpgrade query param is present to show upgrade modal
    const searchParams = new URLSearchParams(location.search);
    const shouldShowUpgradeModal = location.hash === '#upgrade' || searchParams.get('showUpgrade') === 'true';
    const billingTab = <Billing showUpgradeOnMount={shouldShowUpgradeModal} />;

    const subscriptionTab = organisation?.id ? (
        <SubscriptionOverview organisationId={organisation.id.toString()} />
    ) : null;

    // Build tabs array conditionally
    const tabs = [
        {
            label: intl.formatMessage({ id: 'organisation.tabs.details' }),
            key: 'details',
            children: detailsTab,
        },
        {
            label: intl.formatMessage({ id: 'organisation.tabs.users' }),
            key: 'users',
            children: usersTab,
        },
        {
            label: intl.formatMessage({ id: 'organisation.tabs.brandContext', defaultMessage: 'Brand Knowledge' }),
            key: 'brand-knowledge',
            children: <BrandKnowledge brandId={firstBrandId} />,
        },
        {
            label: intl.formatMessage({ id: 'organisation.tabs.agency', defaultMessage: 'Agency' }),
            key: 'agency',
            children: <AgencyRelationship />,
        },
        // Subscription tab only shown when bypass is enabled (no billing tab available)
        ...(bypassEnabled ? [{
            label: intl.formatMessage({ id: 'organisation.tabs.subscription', defaultMessage: 'Subscription' }),
            key: 'subscription',
            children: subscriptionTab,
        }] : []),
        // Deprecated: AI Usage tab replaced by AI Expert Credits in Subscription tab
        // {
        //     label: 'AI Usage',
        //     key: 'ai-usage',
        //     children: aiUsageTab,
        // },
        // Only show Billing tab if bypass is not enabled
        ...(!bypassEnabled ? [
            {
                label: 'Billing & Subscriptions',
                key: 'billing',
                children: billingTab,
            }
        ] : []),
        ...(showApiKeys ? [{
            label: intl.formatMessage({ id: 'organisation.tabs.apiKeys' }),
            key: 'apiKeys',
            children: apiKeysTab,
        }] : []),
    ];

    // Get active tab from URL parameters and update when location changes
    const urlParams = new URLSearchParams(location.search);
    const rawTabFromUrl = urlParams.get('tab') || 'details';
    // Old bookmarks may still point at the hidden API Keys tab.
    const tabFromUrl = rawTabFromUrl === 'apiKeys' && !showApiKeys ? 'details' : rawTabFromUrl;
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const navigate = useNavigate();

    // Update active tab when URL changes
    useEffect(() => {
        setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    // Handle tab change - update both state and URL
    const handleTabChange = (key: string) => {
        setActiveTab(key);
        // Update URL to reflect tab change
        const newParams = new URLSearchParams(location.search);
        newParams.set('tab', key);
        navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    };

    // Show loading state only on initial load or if no organization after initial load
    if ((initialLoad && loading) || (!initialLoad && !organisation)) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                        <div style={{ fontSize: '16px', marginTop: '16px' }}>Loading organization data...</div>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    { title: intl.formatMessage({ id: 'section.dashboardName' }) },
                    { title: intl.formatMessage({ id: 'section.organisationName' }) }
                ]}
            />
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <h2>{organisation?.name}</h2>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    type="card"
                    size="middle"
                    items={tabs}
                />
            </div>
        </Content>
    );
}

export default OrganisationPage;
